/**
 * Create Job Modal
 *
 * Multi-step wizard for dispatchers to create jobs with pricing adjustments.
 *
 * Steps:
 * 1. Customer Selection - Search/select existing customer or create new
 * 2. Service Details - Service type, container size, schedule, location
 * 3. Pricing - Line item breakdown with inline editing and deviation warnings
 * 4. Review - Final review before submission
 *
 * DDD Compliance:
 * - Uses application service hooks (useCreateJob)
 * - No business logic in component
 * - Validates input before submission
 */

import { useState } from 'react';
import { ApiError } from '@/services/api';
import { JobValidation } from '@/application/service-provider/validation/JobValidation';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCreateJobAsDispatcher } from '@/application/service-provider/hooks/useDispatcherOperations';
import type { CreateJobAsDispatcherInput } from '@/application/service-provider/hooks/useDispatcherOperations';
import { PricingAdjustmentStep } from './PricingAdjustmentStep';
import { JobTypeSelector } from './JobTypeSelector';
import { DynamicJobForm } from './DynamicJobForm';
import { validateCustomFields } from '@/lib/customFieldValidation';
import { getMockJobTypeById } from '@/lib/mockData/jobTypes';
import type { ServiceType, ContainerSize, TimeWindow, Priority } from '@/types/provider-job';

interface CreateJobModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type WizardStep = 'customer' | 'service' | 'pricing' | 'review';

interface CustomerData {
  customerId?: string;
  name: string;
  phone: string;
  email?: string;
}

interface ServiceData {
  serviceType: ServiceType;
  containerSize: ContainerSize;
  materialType: string;
  scheduledDate: string;
  timeWindow?: TimeWindow;
  priority: Priority;
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  specialInstructions?: string;
  jobTypeId?: string;
  customFields?: Record<string, unknown>;
  [key: string]: unknown;
}

interface PricingData {
  base: number;
  deliveryFee?: number;
  disposalFee?: number;
  additionalFees?: number;
  discount?: number;
}

export function CreateJobModal({ isOpen, onClose }: CreateJobModalProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('customer');
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [serviceData, setServiceData] = useState<ServiceData | null>(null);
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [validationErrors, setValidationErrors] = useState<Array<{ field: string; message: string }>>([]);

  const createJob = useCreateJobAsDispatcher();

  const handleNext = () => {
    if (currentStep === 'customer') setCurrentStep('service');
    else if (currentStep === 'service') setCurrentStep('pricing');
    else if (currentStep === 'pricing') setCurrentStep('review');
  };

  const handleBack = () => {
    if (currentStep === 'review') setCurrentStep('pricing');
    else if (currentStep === 'pricing') setCurrentStep('service');
    else if (currentStep === 'service') setCurrentStep('customer');
  };

  const handleSubmit = async () => {
    if (!customerData || !serviceData) return;

    // Client-side validation using application service
    const validationErrors = JobValidation.validateJobCreation(customerData, serviceData);

    if (validationErrors.length > 0) {
      setValidationErrors(validationErrors);
      return;
    }

    // Validate custom fields if job type is selected
    if (serviceData.jobTypeId && serviceData.customFields) {
      const jobType = getMockJobTypeById(serviceData.jobTypeId);
      if (jobType) {
        const fieldErrors = validateCustomFields(
          serviceData.customFields,
          jobType.fieldDefinitions
        );

        if (Object.keys(fieldErrors).length > 0) {
          setValidationErrors([
            { field: 'customFields', message: 'Please fix custom field errors' },
          ]);
          return;
        }
      }
    }

    // Simple datetime conversion - backend will normalize and validate
    const scheduledDate = new Date(serviceData.scheduledDate).toISOString();

    // Build dispatcher input following backend CreateJobAsDispatcherSchema
    const input: CreateJobAsDispatcherInput = {
      // Service details
      serviceType: serviceData.serviceType,
      containerSize: serviceData.containerSize,
      materialType: serviceData.materialType,
      scheduledDate: scheduledDate,
      timeWindow: serviceData.timeWindow || undefined,
      priority: serviceData.priority,

      // Location (required by backend)
      location: {
        address: serviceData.location.address,
        city: serviceData.location.city,
        state: serviceData.location.state,
        zipCode: serviceData.location.zipCode,
      },

      // Customer snapshot (required by backend)
      customerSnapshot: {
        customerId: customerData.customerId,
        name: customerData.name,
        phone: customerData.phone,
        email: customerData.email,
      },

      // Notes
      specialInstructions: serviceData.specialInstructions,

      // Custom fields (if job type selected)
      ...(serviceData.jobTypeId && {
        jobTypeId: serviceData.jobTypeId,
        customFields: serviceData.customFields || {},
      }),
    };

    try {
      setValidationErrors([]); // Clear previous errors
      await createJob.mutateAsync(input);
      onClose();
      // Reset form
      setCurrentStep('customer');
      setCustomerData(null);
      setServiceData(null);
      setPricingData(null);
      setValidationErrors([]);
    } catch (error) {
      // Extract validation errors from ApiError
      if (error instanceof ApiError && error.details) {
        setValidationErrors(error.details);
      }
      console.error('Failed to create job:', error);
    }
  };

  const stepTitles: Record<WizardStep, string> = {
    customer: 'Select Customer',
    service: 'Service Details',
    pricing: 'Pricing',
    review: 'Review & Submit',
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Job</DialogTitle>
          <DialogDescription>
            Step {['customer', 'service', 'pricing', 'review'].indexOf(currentStep) + 1} of 4: {stepTitles[currentStep]}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-6">
          {['customer', 'service', 'pricing', 'review'].map((step, index) => (
            <div key={step} className="flex items-center flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep === step
                    ? 'bg-primary-500 text-white'
                    : ['customer', 'service', 'pricing', 'review'].indexOf(currentStep) > index
                      ? 'bg-green-500 text-white'
                      : 'bg-neutral-200 text-neutral-600'
                }`}
              >
                {index + 1}
              </div>
              {index < 3 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    ['customer', 'service', 'pricing', 'review'].indexOf(currentStep) > index
                      ? 'bg-green-500'
                      : 'bg-neutral-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-base font-semibold text-red-800 mb-2">
                  Please correct the following errors before submitting:
                </h3>
                <div className="text-sm text-red-700 space-y-2">
                  {validationErrors.map((error, index) => (
                    <div key={index} className="flex items-start gap-2 p-2 bg-white bg-opacity-50 rounded">
                      <span className="font-mono text-xs text-red-600 mt-0.5">✗</span>
                      <div className="flex-1">
                        <span className="font-semibold">{error.field}:</span>
                        <span className="ml-1">{error.message}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-red-200">
                  <p className="text-xs text-red-600">
                    💡 Tip: Review the highlighted fields above and make sure all required information is complete and properly formatted.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setValidationErrors([])}
                className="ml-3 flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                aria-label="Dismiss errors"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="min-h-[400px]">
          {currentStep === 'customer' && (
            <CustomerSelectionStep
              data={customerData}
              onDataChange={setCustomerData}
            />
          )}
          {currentStep === 'service' && (
            <ServiceDetailsStep
              data={serviceData}
              onDataChange={setServiceData}
            />
          )}
          {currentStep === 'pricing' && (
            <PricingAdjustmentStep
              data={pricingData}
              onDataChange={setPricingData}
              serviceData={serviceData}
            />
          )}
          {currentStep === 'review' && (
            <ReviewStep
              customerData={customerData}
              serviceData={serviceData}
              pricingData={pricingData}
            />
          )}
        </div>

        {/* Footer Actions */}
        <DialogFooter className="gap-2">
          {currentStep !== 'customer' && (
            <Button variant="outline" onClick={handleBack} disabled={createJob.isPending}>
              Back
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={createJob.isPending}>
            Cancel
          </Button>
          {currentStep !== 'review' ? (
            <Button
              onClick={handleNext}
              disabled={
                (currentStep === 'customer' && !customerData) ||
                (currentStep === 'service' && !serviceData)
              }
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!customerData || !serviceData || createJob.isPending}
            >
              {createJob.isPending ? 'Creating...' : 'Create Job'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Step Components

function CustomerSelectionStep({
  data,
  onDataChange,
}: {
  data: CustomerData | null;
  onDataChange: (data: CustomerData) => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isNewCustomer, setIsNewCustomer] = useState(false);

  const handleCustomerChange = (field: keyof CustomerData, value: string) => {
    onDataChange({
      ...(data || { name: '', phone: '' }),
      [field]: value,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Search Existing Customer
        </label>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, phone, or email..."
          className="input-field"
          disabled={isNewCustomer}
        />
        <p className="text-xs text-neutral-500 mt-1">
          Customer search functionality requires backend integration
        </p>
      </div>

      <div className="flex items-center">
        <div className="flex-1 border-t border-neutral-300"></div>
        <span className="px-4 text-sm text-neutral-500">OR</span>
        <div className="flex-1 border-t border-neutral-300"></div>
      </div>

      <div>
        <label className="flex items-center mb-4">
          <input
            type="checkbox"
            checked={isNewCustomer}
            onChange={(e) => setIsNewCustomer(e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm font-medium">Create New Customer</span>
        </label>

        {isNewCustomer && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={data?.name || ''}
                onChange={(e) => handleCustomerChange('name', e.target.value)}
                placeholder="Enter customer name"
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={data?.phone || ''}
                onChange={(e) => handleCustomerChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={data?.email || ''}
                onChange={(e) => handleCustomerChange('email', e.target.value)}
                placeholder="customer@example.com"
                className="input-field"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ServiceDetailsStep({
  data,
  onDataChange,
}: {
  data: ServiceData | null;
  onDataChange: (data: ServiceData) => void;
}) {
  const handleChange = (
    field: keyof ServiceData,
    value: string | TimeWindow | Priority | ServiceData['location'] | Record<string, unknown>
  ) => {
    onDataChange({
      ...(data || {
        serviceType: 'delivery' as ServiceType,
        containerSize: '20Y' as ContainerSize,
        materialType: 'mixed',
        scheduledDate: '',
        priority: 'standard' as Priority,
        location: { address: '', city: '', state: '', zipCode: '' },
      }),
      [field]: value,
    });
  };

  const handleCustomFieldsChange = (customFields: Record<string, unknown>) => {
    handleChange('customFields', customFields);
  };

  return (
    <div className="space-y-6">
      {/* Job Type Selector */}
      <JobTypeSelector
        value={data?.jobTypeId}
        onChange={(typeId) => handleChange('jobTypeId', typeId)}
      />

      {/* Standard Service Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Service Type <span className="text-red-500">*</span>
          </label>
          <select
            value={data?.serviceType || 'delivery'}
            onChange={(e) => handleChange('serviceType', e.target.value)}
            className="input-field"
            required
          >
            <option value="delivery">Delivery</option>
            <option value="pickup">Pickup</option>
            <option value="swap">Swap</option>
            <option value="permanent_placement">Permanent Placement</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Container Size <span className="text-red-500">*</span>
          </label>
          <select
            value={data?.containerSize || '20Y'}
            onChange={(e) => handleChange('containerSize', e.target.value)}
            className="input-field"
            required
          >
            <option value="10Y">10 Yard</option>
            <option value="20Y">20 Yard</option>
            <option value="30Y">30 Yard</option>
            <option value="40Y">40 Yard</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Material Type <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data?.materialType || ''}
            onChange={(e) => handleChange('materialType', e.target.value)}
            placeholder="e.g., Mixed C&D, Concrete, Metal"
            className="input-field"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Scheduled Date <span className="text-red-500">*</span>
          </label>
          <input
            type="datetime-local"
            value={data?.scheduledDate || ''}
            onChange={(e) => handleChange('scheduledDate', e.target.value)}
            className="input-field"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Time Window
          </label>
          <select
            value={data?.timeWindow || ''}
            onChange={(e) => handleChange('timeWindow', e.target.value || '')}
            className="input-field"
          >
            <option value="">Anytime</option>
            <option value="morning">Morning (8am-12pm)</option>
            <option value="afternoon">Afternoon (12pm-4pm)</option>
            <option value="evening">Evening (4pm-8pm)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Priority
          </label>
          <select
            value={data?.priority || 'standard'}
            onChange={(e) => handleChange('priority', e.target.value)}
            className="input-field"
          >
            <option value="low">Low</option>
            <option value="standard">Standard</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Service Address <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={data?.location?.address || ''}
          onChange={(e) => {
            const currentLocation = data?.location || { address: '', city: '', state: '', zipCode: '' };
            handleChange('location', { ...currentLocation, address: e.target.value });
          }}
          placeholder="Street address"
          className="input-field mb-2"
          required
        />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <input
            type="text"
            value={data?.location?.city || ''}
            onChange={(e) => {
              const currentLocation = data?.location || { address: '', city: '', state: '', zipCode: '' };
              handleChange('location', { ...currentLocation, city: e.target.value });
            }}
            placeholder="City"
            className="input-field"
            required
          />
          <input
            type="text"
            value={data?.location?.state || ''}
            onChange={(e) => {
              const currentLocation = data?.location || { address: '', city: '', state: '', zipCode: '' };
              handleChange('location', { ...currentLocation, state: e.target.value });
            }}
            placeholder="State"
            className="input-field"
            required
          />
          <input
            type="text"
            value={data?.location?.zipCode || ''}
            onChange={(e) => {
              const currentLocation = data?.location || { address: '', city: '', state: '', zipCode: '' };
              handleChange('location', { ...currentLocation, zipCode: e.target.value });
            }}
            placeholder="Zip Code"
            className="input-field"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Special Instructions
        </label>
        <textarea
          value={data?.specialInstructions || ''}
          onChange={(e) => handleChange('specialInstructions', e.target.value)}
          placeholder="Any special instructions for the driver..."
          className="input-field"
          rows={3}
        />
      </div>

      {/* Dynamic Custom Fields */}
      {data?.jobTypeId && (
        <DynamicJobForm
          jobTypeId={data.jobTypeId}
          values={data.customFields || {}}
          onChange={handleCustomFieldsChange}
        />
      )}
    </div>
  );
}

function ReviewStep({
  customerData,
  serviceData,
  pricingData,
}: {
  customerData: CustomerData | null;
  serviceData: ServiceData | null;
  pricingData: PricingData | null;
}) {
  const total = pricingData
    ? (pricingData.base || 0) +
      (pricingData.deliveryFee || 0) +
      (pricingData.disposalFee || 0) +
      (pricingData.additionalFees || 0) -
      (pricingData.discount || 0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Customer Info */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-3 pb-2 border-b">
          Customer Information
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-neutral-600">Name</p>
            <p className="text-sm font-medium">{customerData?.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600">Phone</p>
            <p className="text-sm font-medium">{customerData?.phone || 'N/A'}</p>
          </div>
          {customerData?.email && (
            <div className="col-span-2">
              <p className="text-sm text-neutral-600">Email</p>
              <p className="text-sm font-medium">{customerData.email}</p>
            </div>
          )}
        </div>
      </div>

      {/* Service Details */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-3 pb-2 border-b">
          Service Details
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-neutral-600">Service Type</p>
            <p className="text-sm font-medium capitalize">
              {serviceData?.serviceType.replace('_', ' ') || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-neutral-600">Container Size</p>
            <p className="text-sm font-medium">{serviceData?.containerSize || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600">Material Type</p>
            <p className="text-sm font-medium">{serviceData?.materialType || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600">Scheduled Date</p>
            <p className="text-sm font-medium">
              {serviceData?.scheduledDate
                ? new Date(serviceData.scheduledDate).toLocaleString()
                : 'N/A'}
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-sm text-neutral-600">Location</p>
            <p className="text-sm font-medium">
              {serviceData?.location?.address}, {serviceData?.location?.city},{' '}
              {serviceData?.location?.state} {serviceData?.location?.zipCode}
            </p>
          </div>
        </div>
      </div>

      {/* Pricing */}
      {pricingData && (
        <div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-3 pb-2 border-b">
            Pricing Details
          </h3>
          <div className="space-y-2">
            {pricingData.base > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">Base Price</span>
                <span className="text-sm font-medium">${pricingData.base.toFixed(2)}</span>
              </div>
            )}
            {pricingData.deliveryFee && pricingData.deliveryFee > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">Delivery Fee</span>
                <span className="text-sm font-medium">${pricingData.deliveryFee.toFixed(2)}</span>
              </div>
            )}
            {pricingData.disposalFee && pricingData.disposalFee > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">Disposal Fee</span>
                <span className="text-sm font-medium">${pricingData.disposalFee.toFixed(2)}</span>
              </div>
            )}
            {pricingData.additionalFees && pricingData.additionalFees > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">Additional Fees</span>
                <span className="text-sm font-medium">
                  ${pricingData.additionalFees.toFixed(2)}
                </span>
              </div>
            )}
            {pricingData.discount && pricingData.discount > 0 && (
              <div className="flex justify-between text-green-600">
                <span className="text-sm">Discount</span>
                <span className="text-sm font-medium">-${pricingData.discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-neutral-200">
              <span className="text-base font-semibold">Total</span>
              <span className="text-base font-semibold">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
